<template>
  <AppModalWithConfirmation
    :confirm-close="areDataChanged"
    :density="DIALOG_DENSITY.COMPACT"
    :disable-confirm-button="isLoading"
    :show="show"
    :title="t('dashboard.share_dashboard')"
    hide-hero
    @on-confirm="onConfirm"
    @on-close="onClose"
  >
    <template #footer>
      <div class="scd-DialogFooterWrapper">
        <AppButton
          v-if="canEdit"
          :loading="isLoading"
          class="scd-ConfirmButton"
          data-testid="dialog-submit-button"
          type="primary-next"
          @click="onConfirm"
        >
          {{ t('action.done') }}
        </AppButton>
      </div>
    </template>

    <div class="scd-DialogWrapper">
      <AppRadioGroupCommon
        :disabled="!canEdit"
        :model-value="isPrivate"
        :options="getShareTypes"
        name="isPrivate"
        @update:model-value="changeShareType"
      />

      <AppDivider no-margin />

      <template v-if="!isPrivate">
        <div class="scd-DescriptionPublicMode">
          <AppIcon class="scd-Icon" height="24" icon-name="info-next" width="24" />
          <i18n-t keypath="dashboard.share.public.description" scope="global">
            <template #break>
              <br />
            </template>
          </i18n-t>
        </div>

        <AppDivider no-margin />
      </template>

      <div>
        <FormFieldNext v-if="canEdit" :label="t('dashboard.add_people')">
          <AppSelect
            :hidden-items="selectedUserIds"
            :model-value="tableUsersData"
            :offset="[0, -40]"
            :options="shareUserList"
            :placeholder="t('placeholder.search')"
            :search-function="getShareUsers"
            dropdown-search
            item-text="displayName"
            item-value="accountId"
            @update:options="shareUserList = $event"
            @update:model-value="onSelectUser"
          >
            <template #button-content="{ options }">
              <span v-if="isEmpty(options)" class="scd-DarkStateText">
                {{ t('admin.user') }}
              </span>
            </template>
            <template #option-label="{ option }">
              <OwnerFieldOption :option="option" label-key="displayName" />
            </template>
          </AppSelect>
        </FormFieldNext>

        <AppTable
          ref="tableUsersRef"
          :columns="tableColumns"
          :data="tableUsersData"
          :hover-row="tableHoverRow"
          class="scd-UsersTable"
          no-border
          offset-left="20px"
          offset-right="20px"
          style="--head-padding-bottom: 0"
          type="primary-next"
        >
          <template #cell="{ columnKey, item, index }">
            <template v-if="columnKey === TABLE_COLUMNS_KEYS.NAME">
              <UserNameCell :item="item" item-label="displayName" />
            </template>

            <template v-else-if="columnKey === TABLE_COLUMNS_KEYS.ACTION">
              <span v-if="isOwner(item) || !itemMenuActions(item).length" class="scd-DarkStateText">
                {{ getUserRole(item) }}
              </span>
              <DropdownMenu
                v-else
                :items="itemMenuActions(item)"
                :offset="[0, 0]"
                :type="DROPDOWN_MENU_TYPES.DEFAULT_NEXT"
                preferred-position="bottom-end"
                @close="tableHoverRow = -1"
                @open="tableHoverRow = index"
                @item-click="onMenuActionsClick($event, item)"
              >
                <template #activator>
                  <AppButton
                    class="scd-RoleButton"
                    icon-after="arrow-down-black-next"
                    size="sm"
                    type="ghost"
                    without-activity-state
                  >
                    <span class="scd-DarkStateText">
                      {{ getUserRole(item) }}
                    </span>
                  </AppButton>
                </template>
              </DropdownMenu>
            </template>
          </template>
        </AppTable>

        <CopyLinksList v-if="!isCrossPlatformApp" :links-list="linksForShare">
          <template #before-list>
            <AppDivider no-margin />
          </template>
        </CopyLinksList>
      </div>
    </div>
  </AppModalWithConfirmation>
</template>

<script setup>
import { cloneDeep, isEmpty, isEqual } from 'lodash'
import { computed, inject, nextTick, ref, watch, shallowRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import CustomDashboardApiHandler from '@/api/custom-dashboard'
import { scrollSmooth } from '@/composables/visualEffects'
import { ROUTE_PATHS } from '@/routes/route-helpers'
import { tracker } from '@/tracking/amplitude'
import { EVENT_CATEGORIES } from '@/tracking/amplitude-helpers'
import { ACTIONS_KEYS } from '@/utils/actions-keys'
import { AZURE_DEVOPS_SDK } from '@/utils/azure-devops-helpers/general'
import { DIALOG_DENSITY } from '@/utils/components-configurations/app-dialog'
import { DROPDOWN_MENU_TYPES } from '@/utils/components-configurations/dropdown-menu'
import { USER_SHARING_ROLE } from '@/utils/custom-dashboard-helper'
import { MENU_ITEMS_GROUPS } from '@/utils/dropdown-menu'
import { ASSIGNEE_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import {
  composeForgeAppUrl,
  composeUrlSegments,
  replaceAzureLocationHash,
  replaceForgeLocationHash
} from '@/utils/external-link-helper'
import {
  isCrossPlatformAppInjectionKey,
  isJiraAppInjectionKey,
  isWebAppInjectionKey
} from '@/utils/injection-keys'
import { showNotify } from '@/utils/notify'
import { TABLE_COLUMNS_KEYS } from '@/utils/table-columns'
import { usePlansLimitations } from '@/utils/web-app/plans-limitations'
import { getAtlassianBaseUrl } from '@jira/util'
import { IS_DEVELOPMENT } from '@root/app-modes'

import AppModalWithConfirmation from '@/components/AppModalWithConfirmation'
import FormFieldNext from '@/components/form/FormFieldNext'
import OwnerFieldOption from '@/components/form/OwnerFieldOption'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppDivider from '@/components/ui/AppDivider'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import AppRadioGroupCommon from '@/components/ui/AppRadioGroup/AppRadioGroupCommon'
import AppSelect from '@/components/ui/AppSelect/AppSelect'
import { useLinksList } from '@/components/ui/CopySharedLinkModal/composables'
import CopyLinksList from '@/components/ui/CopySharedLinkModal/CopyLinksList'
import DropdownMenu from '@/components/ui/DropdownMenu/DropdownMenu'
import AppTable from '@/components/ui/Table/AppTable'
import UserNameCell from '@/views/workspaces/settings/plugin-users/UserNameCell'

defineOptions({
  name: 'ShareCustomDashboard'
})

const props = defineProps({
  show: {
    type: Boolean
  },
  dashboardForShare: {
    type: Object,
    default: () => ({})
  },
  canEdit: {
    type: Boolean
  },
  canEditOwner: {
    type: Boolean
  }
})

const shareCustomDashboardSnapshot = shallowRef(null)

const { customDashboardPublicLimitations } = usePlansLimitations()
const isPrivate = ref(false)

const totalPublicDashboards = ref(0)
const getTotalDashboardCount = async () => {
  const customDashboardApi = new CustomDashboardApiHandler()
  try {
    const { count } = await customDashboardApi.getDashboardCount({ isPrivate: false })
    totalPublicDashboards.value = count
  } catch (error) {
    handleError({ error })
  }
}

const getChangeDetectPayload = () => {
  const usersWithoutOwner = generateUsersList(tableUsersData.value.filter(user => !isOwner(user)))

  const newOwnerAccountId = tableUsersData.value.find(
    user => user[ASSIGNEE_ENTITY_KEYS.ROLE_ID] === USER_SHARING_ROLE.OWNER
  )?.[ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID]

  return {
    isPrivate: isPrivate.value,
    newOwnerAccountId:
      props.dashboardForShare.ownerId !== newOwnerAccountId ? newOwnerAccountId : null,
    users: usersWithoutOwner
  }
}

const getFullPayload = () => {
  return {
    dashboardId: props.dashboardForShare.id,
    ...getChangeDetectPayload()
  }
}

const areDataChanged = computed(() => {
  const left = getChangeDetectPayload()
  left.users.sort((a, b) => a.accountId.localeCompare(b.accountId))
  return !isEqual(left, shareCustomDashboardSnapshot.value)
})

const isLimitExceeded = computed(() =>
  customDashboardPublicLimitations.value.isLimitExceeded({
    count: totalPublicDashboards.value
  })
)

const isDisablePublicButton = computed(() => {
  return isLimitExceeded.value && isPrivate.value
})

const getDisabledButtonTooltip = computed(() =>
  customDashboardPublicLimitations.value.getDisabledButtonTooltip()
)

const emit = defineEmits(['update:show', 'on-confirm'])
const isLoading = ref(false)

const onConfirm = async () => {
  try {
    isLoading.value = true
    const customDashboardApi = new CustomDashboardApiHandler()

    const trackingPayload = {
      id: props.dashboardForShare.id,
      mode: isPrivate.value ? 'private' : 'public',
      category: EVENT_CATEGORIES.DASHBOARD
    }

    await customDashboardApi.updateMembers(getFullPayload())

    showNotify({
      type: 'success'
    })

    tracker.logEvent('shared custom dashboard', trackingPayload)

    onClose()
    emit('on-confirm')
  } catch (error) {
    handleError({ error })
  } finally {
    isLoading.value = false
  }
}

const onClose = () => {
  emit('update:show', false)
}

const { t } = useI18n()
const getShareTypes = computed(() => [
  {
    label: 'workspaces.public',
    value: false,
    icon: 'public-ws',
    disabled: isDisablePublicButton.value,
    tooltipContent: isDisablePublicButton.value && getDisabledButtonTooltip.value
  },
  {
    label: 'workspaces.private',
    value: true,
    icon: 'lock-next'
  }
])

const shareUserList = ref([])

const generateUsersList = users =>
  users.map(user => ({
    accountId: user[ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID],
    roleId: user[ASSIGNEE_ENTITY_KEYS.ROLE_ID]
  }))

watch(
  () => props.show,
  newValue => {
    if (newValue) {
      fetchShareUserList()
      getTotalDashboardCount()

      isPrivate.value = props.dashboardForShare.private
      tableUsersData.value = [
        {
          accountId: props.dashboardForShare.ownerId,
          avatarUrl: props.dashboardForShare.ownerAvatarUrl,
          displayName: props.dashboardForShare.ownerName,
          roleId: USER_SHARING_ROLE.OWNER
        },
        ...cloneDeep(props.dashboardForShare.userShares)
      ]
      shareCustomDashboardSnapshot.value = {
        isPrivate: props.dashboardForShare.private,
        newOwnerAccountId: null,
        users: generateUsersList(props.dashboardForShare.userShares)
      }
      shareCustomDashboardSnapshot.value.users.sort((a, b) =>
        a.accountId.localeCompare(b.accountId)
      )
    }
  }
)

const changeShareType = value => {
  isPrivate.value = value

  if (!value) {
    tableUsersData.value = tableUsersData.value.filter(
      user => user[ASSIGNEE_ENTITY_KEYS.ROLE_ID] !== USER_SHARING_ROLE.VIEWER
    )
  }
}
const getShareUsers = async (searchString = null) => {
  const customDashboardApi = new CustomDashboardApiHandler()
  try {
    const { users } = await customDashboardApi.getShareCandidates({
      dashboardId: props.dashboardForShare.id,
      searchString
    })
    return users
  } catch (error) {
    handleError({ error })
  }
}

const fetchShareUserList = async () => {
  shareUserList.value = await getShareUsers()
}

const store = useStore()
const route = useRoute()

const currentUser = computed(() => store.state.system.userData || {})

const workspaceId = computed(() => route.params.workspaceId)

const isJiraApp = inject(isJiraAppInjectionKey)
const isWebApp = inject(isWebAppInjectionKey)
const isCrossPlatformApp = inject(isCrossPlatformAppInjectionKey)

const router = useRouter()

const shareLinkSegments = computed(() => [
  ROUTE_PATHS.WORKSPACES,
  workspaceId.value,
  ROUTE_PATHS.DASHBOARD,
  ROUTE_PATHS.CUSTOM_DASHBOARD,
  ROUTE_PATHS.CUSTOM_DASHBOARD_ITEM,
  props.dashboardForShare.id
])

const FALLBACK_URL = null

const generateLinkToJira = async () => {
  if (!props.dashboardForShare.id) {
    return FALLBACK_URL
  }

  if (isJiraApp) {
    const result = await replaceForgeLocationHash({
      hash: `/${composeUrlSegments(...shareLinkSegments.value)}`
    })

    return result || FALLBACK_URL
  }

  if (isWebApp) {
    return composeForgeAppUrl({
      siteUrl: getAtlassianBaseUrl(),
      hash: `/${composeUrlSegments(...shareLinkSegments.value)}`
    })
  }

  return FALLBACK_URL
}

const webAppUrl = computed(() => store.state.appContext.urls.webAppUrl)

const generateLinkToWebApp = async () => {
  if (!props.dashboardForShare.id) {
    return FALLBACK_URL
  }

  if (isJiraApp) {
    return composeUrlSegments(webAppUrl.value, '#', ...shareLinkSegments.value)
  }

  if (isWebApp) {
    return composeUrlSegments(
      location.origin,
      router?.options?.history?.base || '',
      ...shareLinkSegments.value
    )
  }

  return FALLBACK_URL
}

const generateLinkToAzureApp = async () => {
  if (!props.dashboardForShare.id) {
    return FALLBACK_URL
  }

  const result = await replaceAzureLocationHash({
    hash: `/${composeUrlSegments(...shareLinkSegments.value)}`
  })

  return result || FALLBACK_URL
}

const { linksForShare } = useLinksList({
  jiraLinkGenerator: generateLinkToJira,
  webAppLinkGenerator: generateLinkToWebApp,
  azureAppLinkGenerator: generateLinkToAzureApp,
  localDevelopmentUrl: () => {
    if (IS_DEVELOPMENT && !AZURE_DEVOPS_SDK) {
      return composeUrlSegments(
        location.origin,
        router?.options?.history?.base || '',
        ...shareLinkSegments.value
      )
    }

    return FALLBACK_URL
  }
})

const tableColumns = [
  {
    title: '',
    key: TABLE_COLUMNS_KEYS.NAME,
    width: 'auto'
  },
  {
    title: '',
    key: TABLE_COLUMNS_KEYS.ACTION,
    width: 75
  }
]
const tableHoverRow = ref(-1)

const tableUsersData = ref([])
const selectedUserIds = computed(() => tableUsersData.value.map(item => item.accountId))

const tableUsersRef = ref(null)
const onSelectUser = async value => {
  await nextTick() // wait for tableUsersData.value update by update:options emit
  const user = shareUserList.value.find(item => item.accountId === value)
  tableUsersData.value.push({
    ...user,
    displayName: user.displayName,
    roleId: isPrivate.value ? USER_SHARING_ROLE.VIEWER : USER_SHARING_ROLE.EDITOR
  })

  await scrollSmooth({ value: tableUsersRef.value.$el.firstChild })
}

const itemMenuActions = user => {
  const actions = [
    {
      name: ACTIONS_KEYS.MAKE_OWNER,
      title: 'dashboard.user_roles.owner',
      icon: 'star',
      group: MENU_ITEMS_GROUPS.ACTIONS
    },
    {
      name: ACTIONS_KEYS.MAKE_EDITOR,
      title: 'dashboard.user_roles.editor',
      icon: 'edit-next',
      group: MENU_ITEMS_GROUPS.ACTIONS
    },
    {
      name: ACTIONS_KEYS.MAKE_VIEWER,
      title: 'dropdown.viewer',
      icon: 'watch-line',
      group: MENU_ITEMS_GROUPS.ACTIONS
    },
    {
      name: ACTIONS_KEYS.REMOVE,
      title: 'action.delete',
      icon: 'delete-next',
      color: 'var(--grade-low-color-next)',
      group: MENU_ITEMS_GROUPS.REMOVING
    }
  ]

  return actions.filter(action => {
    if (action.name === ACTIONS_KEYS.MAKE_OWNER) {
      return props.canEditOwner
    } else if (action.name === ACTIONS_KEYS.MAKE_EDITOR) {
      return user[ASSIGNEE_ENTITY_KEYS.ROLE_ID] === USER_SHARING_ROLE.VIEWER
    } else if (action.name === ACTIONS_KEYS.MAKE_VIEWER) {
      return user[ASSIGNEE_ENTITY_KEYS.ROLE_ID] === USER_SHARING_ROLE.EDITOR && isPrivate.value
    } else if (action.name === ACTIONS_KEYS.REMOVE) {
      return user[ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID] !== currentUser.value.userAccountId
    }
    return true
  })
}

const getUserRole = user => {
  if (user[ASSIGNEE_ENTITY_KEYS.ROLE_ID] === USER_SHARING_ROLE.OWNER) {
    return t('dashboard.user_roles.owner')
  } else if (user[ASSIGNEE_ENTITY_KEYS.ROLE_ID] === USER_SHARING_ROLE.EDITOR) {
    return t('dashboard.user_roles.editor')
  } else {
    return t('dashboard.user_roles.viewer')
  }
}

const findUserByKey = (key, value) => {
  return tableUsersData.value.find(user => user[key] === value)
}
const setUserRole = (user, value) => {
  if (user) {
    user[ASSIGNEE_ENTITY_KEYS.ROLE_ID] = value
  }
}
const onMenuActionsClick = (name, item) => {
  if (name === ACTIONS_KEYS.REMOVE) {
    tableUsersData.value = tableUsersData.value.filter(
      user => user[ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID] !== item[ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID]
    )
  }

  if (name === ACTIONS_KEYS.MAKE_EDITOR) {
    const user = findUserByKey(
      ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID,
      item[ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID]
    )
    setUserRole(user, USER_SHARING_ROLE.EDITOR)
  }

  if (name === ACTIONS_KEYS.MAKE_VIEWER) {
    const user = findUserByKey(
      ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID,
      item[ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID]
    )
    setUserRole(user, USER_SHARING_ROLE.VIEWER)
  }

  if (name === ACTIONS_KEYS.MAKE_OWNER) {
    const currentOwner = findUserByKey(ASSIGNEE_ENTITY_KEYS.ROLE_ID, USER_SHARING_ROLE.OWNER)
    const newOwner = findUserByKey(
      ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID,
      item[ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID]
    )
    if (currentOwner && newOwner) {
      setUserRole(currentOwner, USER_SHARING_ROLE.EDITOR)
      setUserRole(newOwner, USER_SHARING_ROLE.OWNER)
    }
  }

  tableUsersData.value.sort((a, b) => {
    const aRole = a[ASSIGNEE_ENTITY_KEYS.ROLE_ID]
    const bRole = b[ASSIGNEE_ENTITY_KEYS.ROLE_ID]
    if (aRole === USER_SHARING_ROLE.OWNER && bRole !== USER_SHARING_ROLE.OWNER) return -1
    if (bRole === USER_SHARING_ROLE.OWNER && aRole !== USER_SHARING_ROLE.OWNER) return 1
    return 0
  })
}

const isOwner = item => {
  return item[ASSIGNEE_ENTITY_KEYS.ROLE_ID] === USER_SHARING_ROLE.OWNER
}
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/mixins';

.scd-DescriptionPublicMode {
  display: flex;
  gap: 8px;
  color: $dark-3;
  font-size: $fs-14;
  line-height: 20px;
}
.scd-Icon {
  display: flex;
  min-width: 24px;
  height: 24px;
}
.scd-DialogWrapper {
  display: flex;
  flex-direction: column;
  gap: 24px;
  --divider-color: #{$grey-3-next};
  &:deep(.tb-Header) {
    &:after {
      display: none;
    }
  }
}
.scd-DialogFooterWrapper {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.scd-ConfirmButton {
  margin-left: auto;
}

.scd-DarkStateText {
  color: $dark-3;
}

.scd-UsersTable {
  margin: 20px 0 0 0;
  &:deep(.tb-Table) {
    max-height: 225px;
    overflow: auto;
    position: relative;
    @include styled-native-scrollbar();
  }
}
.scd-RoleButton {
  justify-content: space-between;
  width: 100%;
}
</style>
